/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

const UTILS = (() => {
  const OnElementMutated = (selector, config, callback) => {
    var observer = new MutationObserver(callback);

    // Notify me of everything!
    var observerConfig = {
      attributes: config.attributes || true, 
      childList: config.childList || true, 
      characterData: config.characterData || true 
    };

    // Node, config
    // In this case we'll listen to all changes to body and child nodes
    var targetNodes = document.querySelectorAll(selector);
    targetNodes.forEach(function(node) {
      observer.observe(node, observerConfig);
    });  
  };  

  const OnElementLoad = (selector, callback) => {
    let count = 0
    var t = setInterval(function() {
      var element = document.querySelector(selector);
      count += 100;
      if (
        (element !== null && element !== undefined) || 
        count > 100 * 10 * 3
      ) {
        clearInterval(t);
        callback(element);
      }
    }, 100);
  };

  const AddItemsToCart = (itemsToAdd) => {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items: itemsToAdd })
    });
  };

  const GetCartJson = () => {
    return fetch('/cart.js').then((res) => res.json());
  }

  return {
    OnElementMutated,
    OnElementLoad,
    AddItemsToCart,
    GetCartJson
  };
})();

(() => { 
  const QuantityButtons = () => {
    let containers = [].concat(
      Array.from(document.querySelectorAll('.shopify-section--main-product')),
      Array.from(document.querySelectorAll('.shopify-section--featured-product'))
    );
    if (!containers.length) return;

    containers.forEach(($container) => {
      let $QtyBtnsContainer = $container.querySelector('.quantity-buttons');
      if (!$QtyBtnsContainer) { 
        return;
      }
      
      if ($QtyBtnsContainer.classList.contains('qty--initialized')) {
        return;
      }

      let $qtyBtns = $container.querySelectorAll('.quantity-buttons:not(:first-of-type) .qty-btn-wrap');
      if ($QtyBtnsContainer && $qtyBtns) {
        // Insert buttons to first container
        $qtyBtns.forEach(($btn) => {
          $QtyBtnsContainer.insertAdjacentElement('beforeend', $btn);
        });
        $QtyBtnsContainer.insertAdjacentHTML('beforebegin', '<div class="quantity-title">Quantity:</div>');

        // now show container
        $QtyBtnsContainer.classList.remove('visually-hidden');
        $QtyBtnsContainer.classList.add('qty--initialized');

        // quantity change event handler
        $QtyBtnsContainer.querySelectorAll('input[type=radio]').forEach(($qty) => {
          $qty.addEventListener('change', (e) => {
            var value = e.currentTarget.value;
            var $input = $container.querySelector(`[data-option="quantity"] input[value="${value}"]`);
            if ($input) {
              $input.checked = true;
              setTimeout(() => {
                $input.dispatchEvent(new Event('change', { bubbles: true }));
              }, 0);
            }
          });
        });

        // on page load, update selected variant
        let $option_quantity = $container.querySelector('.product-form__option-selector[data-option="quantity"]');
        if ($option_quantity) {
          let selected_quantity = $option_quantity.querySelector('input[type=radio]:checked').value;
          let $qty_btn_selected = $container.querySelector('.quantity-buttons').querySelector('input[type=radio]:checked');

          if ($qty_btn_selected) {
            if (selected_quantity !== $qty_btn_selected.value) {
              let $qty_btn_input = $container.querySelector('.quantity-buttons').querySelector(`input[value="${selected_quantity}"]`);
              $qty_btn_input.checked = true;
              $qty_btn_input.dispatchEvent(new Event('change', { bubbles: true }));
            }
            else { 
              $qty_btn_selected.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      }
    });
  }

  const StickyATCButtonEvents = () => {
    document.querySelectorAll('#StickyAddToCart').forEach(($btn) => {
      $btn.addEventListener('click', (e) => { 
        e.preventDefault();
        e.stopPropagation();

        let $productVariants = document.querySelector('product-variants');
        let $formButtons = document.querySelector('.product-form__buy-buttons');
        let scrollTo;
        if ($productVariants) {
          scrollTo = $productVariants.offsetTop;
        }
        else {
          scrollTo = $formButtons.offsetTop;
        }

        let isMobile = window.matchMedia("(max-width: 600px)").matches;
        if ($btn.dataset.scrollDesktop || $btn.dataset.scrollMobile) {
          scrollTo = !isMobile ? $btn.dataset.scrollDesktop : $btn.dataset.scrollMobile;
        }

        window.scrollTo({
          top: scrollTo,
          behavior: 'smooth'
        });
      });
    });
  }

  const MediaQueryEvents = () => {
    const mediaQueryList = window.matchMedia('(max-width: 600px)');

    document.dispatchEvent(new CustomEvent('view:resized', { detail: {target: null, isMobileView: mediaQueryList.matches} }));

    mediaQueryList.addEventListener('change', (e) => {
      document.dispatchEvent(new CustomEvent('view:resized', { detail: {target: e.target, isMobileView: e.matches} }));
    });
  }

  const HideProductContentFirstTab = () => { 
    let $el1 = document.querySelector('.shopify-section--product-content .product-tabs__tab-item-wrapper:first-of-type .collapsible-toggle');
    if ($el1) {
      $el1.ariaExpanded = false;
    }
    let $el2 = document.querySelector('.shopify-section--product-content .product-tabs__tab-item-wrapper:first-of-type collapsible-content');
    if ($el2) {
      $el2.removeAttribute('open');
    }
  }
  const ShowProductContentFirstTab = () => { 
    let $el1 = document.querySelector('.shopify-section--product-content .product-tabs__tab-item-wrapper:first-of-type .collapsible-toggle');
    if ($el1) {
      $el1.ariaExpanded = true;
    }
    let $el2 = document.querySelector('.shopify-section--product-content .product-tabs__tab-item-wrapper:first-of-type collapsible-content');
    if ($el2) {
      $el2.setAttribute('open', 'open');
    }
  }

  const CustomEventHandlers = () => {
    document.addEventListener('view:resized', (e) => { 
      let isMobileView = e.detail.isMobileView;
      let showProductContentFirstTab = true;
      if (document.querySelector('[data-mobile_open_tab_default]')) {
        showProductContentFirstTab = JSON.parse(document.querySelector('[data-mobile_open_tab_default]').dataset.mobile_open_tab_default);
      }

      ShowProductContentFirstTab();
      if (isMobileView && !showProductContentFirstTab) {
        HideProductContentFirstTab();
      }
    });
  }

  const AfterPayPlacements = () => {
    setTimeout(function () { 
      document.querySelectorAll('afterpay-placement').forEach((afterpay) => {
        let afterpayLogo = afterpay.shadowRoot.querySelector('.afterpay-logo');
        if (afterpayLogo) {
          afterpayLogo.insertAdjacentHTML('beforebegin', '<span>pay in 4 interest-free payments with </span>');
        }

        let afterpayText2 = afterpay.shadowRoot.querySelector('.afterpay-text2');
        if (afterpayText2) {
          afterpayText2.remove();
        }

        afterpay.classList.remove('visually-hidden');
      });
    });
  }

  const ProductPartialPayments = () => {
    let updatePartialPrice = ($item, price) => {
      let partialPrice = formatMoney(price / 4);
      let $variantPartialPrice = $item.querySelector('.variant-partial-price');
      if ($variantPartialPrice) {
        $variantPartialPrice.innerHTML = `<strong>${partialPrice}</strong>`;
      }
      else {
        $item.innerHTML = $item.innerHTML.replaceAll('[PRICE/4]', `<span class="variant-partial-price"><strong>${partialPrice}</strong></span>`);
      }
      $item.classList.remove('visually-hidden');
    }
    
    document.querySelectorAll('.partial-payment-terms').forEach(($item) => { 
      let price = $item.dataset.price;
      updatePartialPrice($item, price);

      document.addEventListener('variant:changed', function (event) {
        let variant = event.detail.variant;
        updatePartialPrice($item, variant.price);
      });
    });
  }

  const CustomProductMedia = () => {
    document.addEventListener('flickity-loaded', () => {
      setTimeout(() => {
        let flickity = ThemeFlickity.data('.product__media-list--custom');
        if (!flickity) return;
        let $media = flickity.slider.closest('product-media');
        let $thumbs = $media.querySelectorAll('.product__thumbnail-item');

        // add thumb data-index, event handler
        $thumbs.forEach(($thumb, index) => {
          $thumb.setAttribute('data-index', index);

          $thumb.addEventListener('click', (e) => { 
            let slideIndex = e.currentTarget.dataset.index;
            flickity.select(slideIndex);
          });
        });

        // slide change event
        flickity.on('change', (slideIndex) => { 
          $thumbs.forEach(($thumb) => $thumb.removeAttribute('aria-current'));
          $thumbs[slideIndex].setAttribute('aria-current', 'true');
        });
      }, 0);
    });
  }

  const DOMReady = () => {
    console.log('READY!');
    QuantityButtons();
    
    var shopify_events = ['shopify:section:load', 'shopify:section:unload', 'shopify:block:select', 'shopify:block:deselect'];
    shopify_events.forEach(function(event_type) {
      document.addEventListener(event_type, function(e) {
        QuantityButtons();
      });
    });

    StickyATCButtonEvents();
    CustomEventHandlers();
    MediaQueryEvents();
    //AfterPayPlacements();
    ProductPartialPayments();
    CustomProductMedia();
  }

  document.addEventListener('alpine:init', () => {
    Alpine.data('AccessoriesUpsells', () => ({
      $productFormWrap: null,
      $app_form: null,
      $btn_atc: null,
      P_UPSELLS: P_UPSELLS,
      main_product: P_UPSELLS.main_product,
      show_btn_price: false,
      form_type: '',
      mounted() {
        let am = this
        if (am.P_UPSELLS.upsells.length == 0) return

        am.$productFormWrap = am.$el.closest('.product__info')
        am.show_btn_price = JSON.parse(am.$el.dataset.show_btn_price)
        am.form_type = am.$el.dataset.form_type;

        if (am.form_type == 'app') {
          document.addEventListener('widebundle:loaded', (e) => {
            // console.log(e)
            am.MoveUpsells()
          })
        }
        else {
          am.MoveUpsells()
        }
      },
      MoveUpsells() {
        let am = this
        let $btn_atc
        if (am.form_type == 'app') {
          am.$app_form = am.$productFormWrap.querySelector('#new-form')
          $btn_atc = am.$app_form.querySelector('[data-add-to-cart]')
        }
        else {
          $btn_atc = am.$productFormWrap.querySelector('[data-product-add-to-cart-button]')
        }
        am.$btn_atc = $btn_atc

        $btn_atc.insertAdjacentElement('beforebegin', am.$el)
        am.$el.classList.remove('visually-hidden')

        am.InitEventHandlers()
        am.UpdateButton()
      },
      UpdateButton() {
        let am = this
        if (!am.show_btn_price) return

        am.$btn_atc.innerHTML = '<span class="addToCart-w-price"><span>Add To Cart</span><span class="btn-price"><span class="total-price"></span><span class="total-compare-price"></span></span>';

        am.UpdateButtonPrice()

        // on main variant change, update total price
        document.addEventListener('variant:changed', function(event) {
          let variant = event.detail.variant
          am.main_product.selected_variant = variant
          am.UpdateButtonPrice()
        })    
      },
      UpdateButtonPrice() {
        let am = this
        if (!am.show_btn_price) return
        
        let product = am.main_product
        let mainVariant = product.selected_variant;
        let totalPrice = mainVariant.price;
        let totalComparePrice = mainVariant.compare_at_price;
        let $selectedUpsells = [...am.$el.querySelectorAll('input[name="accessories_upsells"]:checked')];
        $selectedUpsells.forEach(($item) => {
          let qty = parseInt($item.closest('.upsell-item').querySelector('.qty').value);
          let item_price = $item.dataset.variantPrice;
          let item_comparePrice = $item.dataset.variantComparePrice || item_price;
          totalPrice += qty * item_price;
          totalComparePrice += qty * item_comparePrice;
        });
  
        let $totalComparePrice = am.$btn_atc.querySelector('.total-compare-price') || am.$btn_atc.querySelector('span:first-of-type');
        if ($totalComparePrice) {
          $totalComparePrice.innerHTML = formatMoney(totalComparePrice);
          $totalComparePrice.classList.toggle('visually-hidden', !totalComparePrice || totalComparePrice <= totalPrice);
        }
        let $totalPrice = am.$btn_atc.querySelector('.total-price') || am.$btn_atc.querySelector('span:last-of-type');
        if ($totalPrice) {
          $totalPrice.innerHTML = formatMoney(totalPrice);
        }
  
        // afterpay
        am.$productFormWrap.querySelectorAll('.product-form afterpay-placement').forEach(($afterpay) => {
          $afterpay.dataset.amount = totalPrice / 100
        })
      },
      InitEventHandlers() {
        let am = this
        // On main item added, add selected upsells to cart
        let main_product_id = am.P_UPSELLS.main_product.id;
        document.addEventListener('variant:added', function(e) {
          var variant = e.detail.variant; // Get the variant that was added
          if (variant.product_id == main_product_id) {
            let itemsToAdd = Array.from(am.$el.querySelectorAll('[name=accessories_upsells]:checked')).map(($item) => {
              let $accessory = $item.closest('.upsell-item');
              let $qty = $accessory.querySelector(`select[data-id="${ $item.id }"]`);
              return {
                id: $item.dataset.variantId,
                quantity: $qty.value || 1
              }
            });
    
            if (!itemsToAdd.length) return;
            
            setTimeout(async () => {
              let add_req = await UTILS.AddItemsToCart(itemsToAdd);
              if (add_req.ok) {
                let cart = await UTILS.GetCartJson();
                document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                  bubbles: true,
                  detail: {cart}
                }));
                setTimeout(am.UpdateButtonPrice.bind(am), 1000);
              }  
            }, 500);
          }
        });

        // Change events on product item
        am.$el.addEventListener('change', (e) => {
          am.UpdateButtonPrice()
          // On Quantity change, Select Upsell item
          if (e.target.classList.contains('qty')) {
            let $input = e.target.closest('.upsell-item').querySelector('input[type=checkbox]')
            $input.checked = true
            $input.dispatchEvent(new Event('change', {bubbles: true}))
          }

          // On Variant Change
          if (e.target.classList.contains('variant-swatch__radio')) {
            e.target.closest('.upsell-item').querySelector('[data-variant-id]').dataset.variantId = e.target.dataset.variant_id;
            e.target.closest('.upsell-item').querySelector('.upsell-image img').src = e.target.dataset.variant_image;
          }          
        });
      }
    }));
  });

  document.addEventListener('DOMContentLoaded', DOMReady);
})();